import {useCallback, useEffect, useRef, useState} from 'react';
import {ContentEditor} from '@/components/dashboard/pages/landing-page-optimizer/page-editor/LeftSide/EditorSection/content-editor';
import {Notification, notificationType} from '@/components/common-components/components/notification';
import {useStore} from '@/store/root-store';
import {observer} from 'mobx-react-lite';
import styles from '../content-editor.module.scss';
import ShareModal from '../../../blocks/share-modal';
import {ContentBlock, ContentState, EditorState, Modifier, SelectionState} from 'draft-js';
import {decoratedState} from './content-editor/decorators';
import {debounce, isNil} from 'lodash';
import 'firebase/database';
import {
  isScaPublicPage,
} from '@/components/dashboard/pages/landing-page-optimizer/page-editor/LeftSide/EditorSection/content-editor/utils';
import {AiTooltip} from './content-editor/AiTooltip/AiTooltip';
import {AddImageDrawer} from '../../Drawers/AddImageDrawer';
import {addCrossIcon} from '@/utils/htmlToDraft';
import {Modal} from '@/components/common-components';
import styled from 'styled-components';
import {Input} from 'antd';
import {Button} from '@/components/common-components/v2';
import {isExtensionInstalled} from '@/utils/notification';
import {CloseOutlined} from '@ant-design/icons';

interface Props {
  htmlToDraft: any;
}

export const EditorSection = observer(({htmlToDraft}: Props) => {
  const {
    contentOptimizer,
  } = useStore('');
  const {currentPage, contentFragment} = contentOptimizer;
  const {
    focusTermsClassNames,
    setAiTooltip,
    isCopy,
    setEditorHeightOffset,
    editorHeightOffset,
    setShowAiBanner,
    showAiBanner,
    content,
  } = currentPage;
  const {settings: {customer: {isGHLAccount, profile: {isWhitelabel}}}} = useStore('');
  const [showEditAltModal, setShowEditAltModal] = useState(false);
  const [altText, setAltText] = useState('');
  const [element, setElement] = useState(null);
  const [modalEditorStates, setModalEditorStates] = useState(null);
  const [hideButton, setHideButton] = useState(false);
  const [noFirstVisit] = useState(localStorage.getItem('scaDashboardOpened'));

  const handleModalOpen = (e, modalEditorStates, currentAlt) => {
    setShowEditAltModal(true);
    setElement(e);
    setModalEditorStates(modalEditorStates);
    setAltText(currentAlt);
  };

  const handleUpdateAlt = async () => {
    const contentState = modalEditorStates.getCurrentContent();
    const tagkey = element.attributes['data-offset-key'].value.split('-')[0];
    const blockList = modalEditorStates.getCurrentContent().getBlockMap();
    const block = blockList.find(key => key.getKey() == tagkey);
    if (block.getType() == 'atomic') {
      // @ts-ignore
      const next = currentPage?.currentEditorState?.getCurrentContent()?.getBlockAfter(block.getKey());

      if (block && next) {
        const entityKey = block.getEntityAt(0);
        const entity = contentState.getEntity(entityKey);
        const newEntity = entity.set('data', {alt: altText, title: altText});
        // @ts-ignore
        const newContentState = currentPage?.currentEditorState?.getCurrentContent()?.mergeEntityData(entityKey, newEntity.getData());
        const newED = EditorState.push(modalEditorStates, newContentState, 'apply-entity');
        onChange(newED, false, true);
      }
    }
  };

  useEffect(() => {
    const preventingDefaultMenu = e => {
      e.preventDefault();
      promptForLink(currentPage.currentEditorState, e);
      currentPage.setAiTooltip({...currentPage.aiTooltip, show: true, left: currentPage.aiTooltip?.left || e.pageX, top: e.pageY});
    };
    const closeContextMenu = () => {
      currentPage.setAiTooltip({show: false});
    };
    if (document.querySelectorAll('.DraftEditor-editorContainer')[0]?.addEventListener) {
      document.querySelectorAll('.DraftEditor-editorContainer')[0]?.addEventListener('contextmenu', preventingDefaultMenu, false);
    }
    if (document.querySelectorAll('.DraftEditor-editorContainer')[0]?.addEventListener) {
      document.querySelectorAll('.DraftEditor-editorContainer')[0]?.addEventListener('click', closeContextMenu, false);
    }

    return () => {
      document.querySelectorAll('.DraftEditor-editorContainer')[0]?.removeEventListener('contextmenu', preventingDefaultMenu);
      document.querySelectorAll('.DraftEditor-editorContainer')[0]?.removeEventListener('click', closeContextMenu);
    };
  }, []);

  const headerKeywordsRef = useRef(null);

  const updateKeywordsRef = () => {
    if (!isNil(headerKeywordsRef?.current?.offsetHeight)) {
      setTimeout(() => {
        setEditorHeightOffset(editorHeightOffset?.breadcrumbHeight, headerKeywordsRef?.current?.offsetHeight || 0);
      }, 300);
    }
  };

  useEffect(() => {
    updateKeywordsRef();
  }, [headerKeywordsRef?.current]);

  const debouncedChange = debounce((state: EditorState, focusTerms, focusTermsClassNames, isNewDecorations = false, isManualUpdate = false) => {
    // we are then calling the edit content method which will fire the API call and send new state to the BE
    currentPage.editContent(state, isNewDecorations, isManualUpdate);
  }, 2000);

  const debouncedUpdate = useCallback(
    (state: EditorState, focusTerms, focusTermsClassNames, isNewDecorations, isManualUpdate?: boolean) => debouncedChange(state, focusTerms, focusTermsClassNames, isNewDecorations, isManualUpdate),
    // eslint-disable-next-line
        []
  );

  const debouncedHighlightingChange = debounce((state: EditorState, focusTermsClassNames, changeType = null) => {
    let decorated;
    const acceptedTypes = ['insert-fragment', 'insert-characters', 'apply-entity'];
    if (acceptedTypes.includes(changeType)) {
      // we are using the passed editor state (updated state) and doing the decoration
      decorated = decoratedState(state, focusTermsClassNames);
      // we then set that decorated state as current local state
      currentPage.setCurrentEditorState(EditorState.forceSelection(decorated, state.getSelection()));
    }
  }, 500);

  const debouncedHighlighting = useCallback(
    (state: EditorState, focusTermsClassNames, changeType) => debouncedHighlightingChange(state, focusTermsClassNames, changeType),
    // eslint-disable-next-line
        []
  );

  const errorMessage = currentPage.isImportError.isCreatedFromScratch ?
    'Importing from URL failed. You can manually copy & paste the page content in to the editor below' :
    'Importing from URL failed. You can manually copy & paste the page content in to the editor below (technicals data will remain unchanged)';

  const getParams = (editorState: EditorState) => {
    const selection = editorState.getSelection();
    const currentContent = editorState.getCurrentContent();
    const startKey = selection.getStartKey();
    const endKey = selection.getEndKey();
    const startOffset = selection.getStartOffset();
    const endOffset = selection.getFocusOffset() > selection.getAnchorOffset() ? selection.getFocusOffset() : selection.getAnchorOffset();
    // const startOffset = selection.getAnchorOffset() < selection.getFocusOffset() ? selection.getAnchorOffset() : selection.getFocusOffset();
    const prevOffset = startOffset - 1;
    const blockWithLinkAtBeginning = currentContent.getBlockForKey(startKey);
    const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
    const characterList = blockWithLinkAtBeginning.getCharacterList();
    const initialSelectedText = blockWithLinkAtBeginning.getText().slice(startOffset, endOffset);
    const anchorKey = selection.getAnchorKey();
    const blocks = currentContent.getBlockMap();

    return {selection, characterList, prevOffset, startOffset, currentContent, blockWithLinkAtBeginning, linkKey, initialSelectedText, anchorKey, startKey, endKey, blocks};
  };


  const generateSelectedText = (blocks, startOffset, endOffset, startKey, endKey) => {
    let lastWasEnd = false;
    const selectedBlocks = blocks.skipUntil(block => {
      return block?.getKey() === startKey;
    }).takeUntil(block => {
      const result = lastWasEnd;
      if (block?.getKey() === endKey) {
        lastWasEnd = true;
      }
      return result;
    });

    const blockArray: Array<ContentBlock> = [];
    const selectedTextFromBlocks = [];


    selectedBlocks.forEach(block => {
      blockArray.push(block);
    });

    if (blockArray.length == 1) {
      selectedTextFromBlocks.push(blockArray[0].getText().slice(startOffset, endOffset));
    } else {
      blockArray.forEach((block, id) => {
        if (id === 0) {
          selectedTextFromBlocks.push(block.getText().slice(startOffset, block.getLength()));
        } else if (id === (blockArray.length - 1)) {
          selectedTextFromBlocks.push(block.getText().slice(0, endOffset));
        } else {
          selectedTextFromBlocks.push(block.getText());
        }
      });
    }


    const selectedText = selectedTextFromBlocks.join(' ');
    return selectedText;
  };

  const promptForLink = (editorState: EditorState, e: MouseEvent) => {
    const {selection, characterList, prevOffset, startOffset, currentContent, blockWithLinkAtBeginning, linkKey, initialSelectedText, anchorKey, startKey, endKey, blocks} = getParams(editorState);

    // const newSelection = new SelectionState({
    //   anchorKey: selection.getEndKey(),
    //   anchorOffset: selection.getEndOffset(),
    //   focusKey: selection.getEndKey(),
    //   focusOffset: selection.getEndOffset(),
    // });

    const prevChar = characterList.get(prevOffset);
    const nextChar = characterList.get(startOffset);

    let selectedText: string;
    const xSelection = window.getSelection();
    let x;

    let updatedSelection = selection;

    const start = selection.getAnchorOffset() < selection.getFocusOffset() ? selection.getAnchorOffset() : selection.getFocusOffset();
    const end = selection.getFocusOffset() > selection.getAnchorOffset() ? selection.getFocusOffset() : selection.getAnchorOffset();

    const noEntityCase = () => {
      // const start = selection.getAnchorOffset() < selection.getFocusOffset() ? selection.getAnchorOffset() : selection.getFocusOffset();
      // const end = selection.getFocusOffset() > selection.getAnchorOffset() ? selection.getFocusOffset() : selection.getAnchorOffset();

      let noSelectionLeft = null;
      let noSelectionTop = null;
      let noSelectionBottom = null;

      if (xSelection?.rangeCount) {
        const tempX = xSelection?.getRangeAt(0)?.getBoundingClientRect();
        if (tempX.left) noSelectionLeft = tempX.left;
        if (tempX.top) noSelectionTop = tempX.top;
        if (tempX.bottom) noSelectionBottom = tempX.bottom;
      }
      const selectedText = generateSelectedText(blocks, start, end, startKey, endKey);
      setAiTooltip({...currentPage.aiTooltip, selectedText: selectedText.trim(), textValue: selectedText.trim(), updatedSelection: selection, left: noSelectionLeft || 0, top: noSelectionTop || 0, bottom: noSelectionBottom || 0});
    };

    if (!prevChar || !nextChar) {
      const xSelection = window.getSelection();
      x = xSelection?.getRangeAt(0)?.getBoundingClientRect();
      setAiTooltip({...currentPage.aiTooltip, updatedSelection: updatedSelection, bottom: e.clientY, left: e.clientX});
    } else {
      const prevEntity = prevChar?.getEntity();
      const nextEntity = nextChar?.getEntity();

      const entity = prevEntity === nextEntity && prevEntity || !prevEntity && nextEntity;
      let finalPrevOffset = prevOffset;
      let finalNextOffset = startOffset;

      // selectedText = currentBlock.getText().slice(finalPrevOffset, finalNextOffset);
      selectedText = generateSelectedText(blocks, start, end, startKey, endKey);

      if (entity) {
        while (finalPrevOffset > 0) {
          finalPrevOffset--;
          const char = characterList.get(finalPrevOffset);
          if (char.getEntity() !== entity) {
            finalPrevOffset++;
            break;
          }
        }


        const blockLength = blockWithLinkAtBeginning.getLength();
        while (finalNextOffset < blockLength) {
          finalNextOffset++;

          const char = characterList.get(finalNextOffset);
          if (char?.getEntity() !== entity) {
            break;
          }
        }
        const finalAnchorOffset = finalPrevOffset === -1 ? 0 : finalPrevOffset;

        updatedSelection = new SelectionState({
          anchorKey: anchorKey, // key of block
          anchorOffset: finalAnchorOffset,
          focusKey: anchorKey,
          focusOffset: finalNextOffset, // key of block
          hasFocus: true,
          // isBackward: false // isBackward = (focusOffset < anchorOffset)
        });


        setAiTooltip({...currentPage.aiTooltip, updatedSelection: updatedSelection});

        // selectedText = currentBlock.getText().slice(finalAnchorOffset, finalNextOffset);
        selectedText = generateSelectedText(blocks, finalPrevOffset, finalNextOffset, startKey, endKey);

        if (xSelection?.rangeCount) {
          x = xSelection?.getRangeAt(0)?.getBoundingClientRect();
          if (x.left) {
            setAiTooltip({...currentPage.aiTooltip, isLink: true, selectedText: selectedText.trim(), left: x.left, height: x.height, width: x.width, bottom: x.bottom, updatedSelection: updatedSelection});
          }
        }
      } else {
        updatedSelection = new SelectionState({
          anchorKey: anchorKey, // key of block
          anchorOffset: finalPrevOffset,
          focusKey: anchorKey,
          focusOffset: finalNextOffset, // key of block
          hasFocus: true,
          // isBackward: false // isBackward = (focusOffset < anchorOffset)
        });

        if (xSelection?.rangeCount) {
          if (initialSelectedText) {
            x = xSelection?.getRangeAt(0)?.getBoundingClientRect();
            if (x.left) {
              setAiTooltip({...currentPage.aiTooltip, isLink: true, selectedText: selectedText.trim(), left: x.left, height: x.height, width: x.width, bottom: x.bottom, updatedSelection: updatedSelection});
            }
          }
        }
        noEntityCase();
      }
    }
    let url = '';

    if (linkKey) {
      const linkInstance = currentContent.getEntity(linkKey);
      url = linkInstance.getData().url;
      url = url?.startsWith('//') ? url?.slice(2) : url;
    }

    setAiTooltip({
      ...currentPage.aiTooltip,
      urlValue: url,
      textValue: selectedText,
    });
  };

  useEffect(() => {
    if (currentPage?.getCurrentEditorState?.getCurrentContent()?.hasText() || content.aiInProgress) {
      if (showAiBanner) setShowAiBanner(false);
    } else {
      if (!showAiBanner) setShowAiBanner(true);
    }
  }, [currentPage?.getCurrentEditorState, content.aiInProgress]);

  // This method is triggered by updating the DraftJs editor
  const onChange = (editorState: EditorState, isNewDecorations: boolean = false, isManualUpdate: boolean = false) => {
    const currentContent = currentPage.currentEditorState.getCurrentContent();
    const newContent = editorState.getCurrentContent();
    const changeType = editorState.getLastChangeType();
    currentPage.setCurrentEditorState(editorState);

    const lastChangedBlockKey = editorState.getSelection()?.getAnchorKey();
    const lastChangedBlock = currentContent.getBlockForKey(lastChangedBlockKey);
    const lastChangedBlockType = lastChangedBlock?.getType();
    // setting cross icons and event listener for image
    addCrossIcon(editorState, currentPage, onChange, handleModalOpen);
    // We are comparing current editor state with udpated one to make sure the flow only triggers once there is an actual content change (adding removing of words, formatting, linkjng etc.)
    if (currentContent !== newContent || lastChangedBlockType === 'atomic' || isManualUpdate) {
      debouncedHighlighting(editorState, focusTermsClassNames, changeType);
      debouncedUpdate(editorState, currentPage.analytics?.focusTerms, focusTermsClassNames, isNewDecorations, isManualUpdate);

      if (!newContent?.hasText() && !showAiBanner) {
        setShowAiBanner(true);
      } else {
        if (showAiBanner) {
          setShowAiBanner(false);
        }
      }
    }
  };

  const insertText = async () => {
    // this method cretates conntentBlock out of fetched BE AI text
    const {contentBlocks, entityMap} = htmlToDraft(contentFragment?.fragment?.html || '');
    // const {contentBlocks, entityMap} = htmlToDraft(contentFragment?.fragment?.html);

    // this is currently selected text
    const selection = currentPage.currentEditorState.getSelection();

    // this is new selection that starts after the seelcted text
    const newSelection = new SelectionState({
      anchorKey: selection.getEndKey(),
      anchorOffset: selection.getEndOffset(),
      focusKey: selection.getEndKey(),
      focusOffset: selection.getEndOffset(),
    });


    // this is fragment that will be created from content blocks generated from htmlToDraft
    const fragment = ContentState.createFromBlockArray(contentBlocks, entityMap).getBlockMap();

    const nonStyledState = Modifier.replaceWithFragment(
      currentPage.currentEditorState.getCurrentContent(),
      newSelection,
      fragment,
    );

    // WE are calculating nodes to toggle blue background on new Ai generated nodes
    let firstNewBlockId: number;
    const currentBlock = selection.getAnchorKey();
    const nextBlock = currentPage.currentEditorState.getCurrentContent().getBlockAfter(selection.getAnchorKey())?.getKey();

    const existingNodes = document.querySelectorAll('[data-block="true"]');
    existingNodes?.forEach((elem, id) => {
      if (elem.getAttribute('data-offset-key') && elem.getAttribute('data-offset-key') == `${currentBlock}-0-0`) {
        firstNewBlockId = id + 1;
      }
    });

    // we update current editor state with new state we created
    const updatedState = EditorState.push(currentPage.currentEditorState, nonStyledState, 'insert-fragment');
    // we uset that new state to mbox, this will trigger some methods via useEffect
    currentPage.setCurrentEditorState(updatedState);
    // we hide ai tooltip
    currentPage.setAiTooltip({show: false});
    // after some time we remove highlighting class from all that new text
    setTimeout(() => {
      const newTextElems = document.querySelectorAll('[data-block="true"]');
      if (newTextElems.length) {
        for (let i = firstNewBlockId; i <= newTextElems.length; i++) {
          if (newTextElems[i]?.getAttribute('data-offset-key') && newTextElems[i]?.getAttribute('data-offset-key') != `${nextBlock}-0-0` && newTextElems[i]?.getAttribute('data-offset-key') != `${currentBlock}-0-0`) {
            newTextElems[i].classList.add('newTextBlock');
          } else {
            return;
          }
        }
      }
    }, 500);

    // we trigger a method that will highlight focus terms inside text and fire api to sync with new editor state
    debouncedUpdate(updatedState, currentPage.analytics?.focusTerms, focusTermsClassNames, false);


    setTimeout(() => {
      const newTextElems = document.querySelectorAll('.newTextBlock');
      if (newTextElems.length) {
        newTextElems.forEach(elem => {
          elem.classList.remove('newTextBlock');
        });
      }
    }, 3500);
  };

  // This method will replace selected text
  const replaceText = async () => {
    // this method cretates conntentBlock out of fetched BE AI text
    const {contentBlocks, entityMap} = htmlToDraft(contentFragment?.fragment?.html);
    // this is currently selected text
    const selection = currentPage.currentEditorState.getSelection();
    // this is fragment that will be created from content blocks generated from htmlToDraft
    const fragment = ContentState.createFromBlockArray(contentBlocks, entityMap).getBlockMap();

    const nonStyledState = Modifier.replaceWithFragment(
      currentPage.currentEditorState.getCurrentContent(),
      selection,
      fragment,
    );

    // WE are calculating nodes to toggle blue background on new Ai generated nodes
    let firstNewBlockId: number;
    const currentBlock = selection.getAnchorKey();
    const nextBlock = currentPage.currentEditorState.getCurrentContent().getBlockAfter(selection.getAnchorKey())?.getKey();
    // WE are calculating nodes to toggle blue background on new Ai generated nodes
    const existingNodes = document.querySelectorAll('[data-block="true"]');
    existingNodes?.forEach((elem, id) => {
      if (elem.getAttribute('data-offset-key') && elem.getAttribute('data-offset-key') == `${currentBlock}-0-0`) {
        firstNewBlockId = id + 1;
      }
    });

    // we update current editor state with new state we created
    const updatedState = EditorState.push(currentPage.currentEditorState, nonStyledState, 'insert-fragment');
    // we uset that new state to mbox, this will trigger some methods via useEffect
    currentPage.setCurrentEditorState(updatedState);
    // we hide ai tooltip
    currentPage.setAiTooltip({show: false});
    // after some time we remove highlighting class from all that new text
    setTimeout(() => {
      const newTextElems = document.querySelectorAll('[data-block="true"]');
      if (newTextElems.length) {
        for (let i = firstNewBlockId; i <= newTextElems.length; i++) {
          if (newTextElems[i]?.getAttribute('data-offset-key') && newTextElems[i]?.getAttribute('data-offset-key') != `${nextBlock}-0-0` && newTextElems[i]?.getAttribute('data-offset-key') != `${currentBlock}-0-0`) {
            newTextElems[i].classList.add('newTextBlock');
          } else {
            return;
          }
        }
      }
    }, 500);
    // we trigger a method that will highlight focus terms inside text and fire api to sync with new editor state
    debouncedUpdate(updatedState, currentPage.analytics?.focusTerms, focusTermsClassNames, false);

    setTimeout(() => {
      const newTextElems = document.querySelectorAll('.newTextBlock');
      if (newTextElems.length) {
        newTextElems.forEach(elem => {
          elem.classList.remove('newTextBlock');
        });
      }
    }, 3500);
    // NEW FLOW END
  };

  function getDifferenceInHours(date1, date2) {
    const diffInMs = Math.abs(date2 - date1);
    return diffInMs / (1000 * 60 * 60);
  }

  function handleShowNotification() {
    const checkDate = localStorage.getItem('extensionButton');
    const getCount = localStorage.getItem('extensionButtonCount');
    if (noFirstVisit && noFirstVisit == 'TRUE') {
      if (checkDate) {
        const converted = Number(checkDate);
        const hoursDifference = getDifferenceInHours(converted, Date.now());
        if (getCount == '3') {
          setHideButton(true);
          localStorage.setItem('extensionButton', 'FALSE');
        } else if (hoursDifference >= 216 && !isExtensionInstalled() && checkDate !== 'FALSE' && getCount == '2') {
          setHideButton(false);
          localStorage.setItem('extensionButton', 'FALSE');
        } else if (hoursDifference >= 48 && !isExtensionInstalled() && checkDate !== 'FALSE' && getCount == '1') {
          setHideButton(false);
        } else if (hoursDifference < 48 && !isExtensionInstalled() && checkDate !== 'FALSE' && getCount == '0') {
          setHideButton(false);
        }
        if (getCount == '2') {
          setHideButton(false);
          localStorage.setItem('extensionButton', 'FALSE');
        }
      } else if (!isExtensionInstalled()) {
        setHideButton(false);
        localStorage.setItem('extensionButton', String(Date.now()));
        localStorage.setItem('extensionButtonCount', '0');
      }
    }
  }

  useEffect(() => {
    if (!isExtensionInstalled()) {
      handleShowNotification();
    }
  }, []);

  const hideBtn = () => {
    setTimeout(() => {
      setHideButton(true);
    }, 500);
  };

  const hideExtensionBtn = () => {
    const checkCount = localStorage.getItem('extensionButtonCount');
    if (checkCount == '0') {
      localStorage.setItem('extensionButtonCount', '1');
    } else if (checkCount == '1') {
      localStorage.setItem('extensionButtonCount', '2');
    } else if (checkCount == '2') {
      localStorage.setItem('extensionButtonCount', '3');
    }
    setTimeout(() => {
      setHideButton(true);
    }, 500);
  };

  return <>
    {/*
    * Show error if importing from url has failed
    * Error text is dynamic and depends if it is created from scratch or not
    */}
    {currentPage.isImportError.show && <div style={{width: '816px', marginTop: '20px', maxWidth: '100%', marginLeft: '10px', marginRight: '10px'}}>
      <Notification type={notificationType.error} text={errorMessage} />
    </div>}
    <AddImageDrawer onChange={onChange} />
    <ContentEditor
      onChange={onChange}
      showAiBanner={showAiBanner}
    />
    {isWhitelabel ? '' : !hideButton && !isExtensionInstalled() && (isGHLAccount === false) && <div className={styles.extensionButton} style={{display: currentPage?.isExportingPage ? 'none' : 'block'}}>
      <div onClick={hideExtensionBtn}><CloseOutlined className={styles.closeIcon} /></div>
      <div className={styles.iconButton}> <img src={'/img/google-color.svg'} /></div>
      <div className={styles.ctaTitle}>Optimize Content in Google Docs <br />
                & Get SEO Analysis on any site.</div>
      <div className={styles.extensionButtonStyle}>
        <a
          onClick={hideBtn}
          href='https://chrome.google.com/webstore/detail/linkgraphs-searchatlas/ahnhkdjjonmjlhbbgaaoehdnhlgcgbab'
          target='_blank' rel='noreferrer'
        >
          <img src={'/img/google-color.svg'} />Get Chrome Extension
        </a>
      </div>
    </div>}
    {!isScaPublicPage() && <AiTooltip onChange={onChange} insertText={insertText} replaceText={replaceText} />}

    {(!isScaPublicPage() && isCopy) && <div className={styles.textInputNotification}>
    Paragraph was copied to clipboard
    </div>}

    <ShareModal />
    <Modal
      visible={showEditAltModal}
      width={500}
      destroyOnClose
      style={{
        height: 'auto',
        margin: 'auto',
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
      }}
      onClose={() => {
        setShowEditAltModal(false);
      }}
    >
      <div>
        <Title>Edit Alt Tag</Title>
        <InputTitle>Alt tag (optional)</InputTitle>
        <InputStyled value={altText} onChange={e => setAltText(e?.target?.value)} />
        <ButtonsWrapper>
          <CancelButton onClick={() => setShowEditAltModal(false)}>Cancel</CancelButton>
          <ButtonStyled onClick={() => {
            handleUpdateAlt();
            setShowEditAltModal(false);
          }}>Save changes</ButtonStyled>
        </ButtonsWrapper>
      </div>
    </Modal>
  </>;
});

const Title = styled.div`
  color: #121212;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const InputTitle = styled.div`
  color: #121212;
  font-size: 12px;
  margin-bottom: 7px;
`;

const InputStyled = styled(Input)`
  width: 100%;
  height: 38px;
  background: #FFFFFF;
  border: 1px solid #D2D2D2;
  border-radius: 8px;
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: end;
  align-items: center;
  margin-top: 19px;
`;

const ButtonStyled = styled(Button)`
  margin: 0px !important;
  background: #2D6CCA;

  &:hover {
    background: #2D6CCA !important;
    opacity: 0.8;
  }
  &:focus {
    background: #2D6CCA !important;
    opacity: 0.8;
  }
`;

const CancelButton = styled.div`
  width: fit-content;
  color: #4E5156;
  font-weight: 500;
  cursor: pointer;
  margin-right: 31px;
`;
